import { SHA1 } from "@noble/hashes/legacy";
import { DeviceToolError } from "../errors";
import { readBlobArrayBuffer } from "../files/ChunkedFileReader";
import type { OperationProgress } from "../types";
import type { WebUsbTransport, TransferOptions } from "../usb/WebUsbTransport";
import { parseAmlogicImage, type AmlogicImageItem, type AmlogicImageReport } from "./AmlogicImageParser";

export type AmlogicState =
  | "DISCONNECTED"
  | "SELECTING_DEVICE"
  | "OPENING_DEVICE"
  | "CLAIMING_INTERFACE"
  | "HANDSHAKING"
  | "READING_DEVICE_INFO"
  | "READY"
  | "PREPARING_IMAGE"
  | "ERASING"
  | "DOWNLOADING"
  | "VERIFYING"
  | "FINALIZING"
  | "RESETTING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export const ADNL_PROTOCOL_IMPLEMENTED = true;
export const ADNL_IDENTIFY_SIGNATURE = new Uint8Array([5, 0, 0, 16, 0, 0, 0, 0]);
export const ADNL_RAW_TRANSFER_LEN = 128 * 1024;
// Use larger WebUSB submissions to reduce browser/driver round-trips. The
// S905W2/S905X4/S928X ADNL builds tested by this app split the transfer into
// normal USB bulk packets internally.
export const ADNL_USB_BLOCK_LEN = 64 * 1024;
export const ADNL_COMPAT_USB_BLOCK_LEN = 8 * 512;
/**
 * Full-package V3 mwrite uses the same bulk path as Flash Partition V3.
 * Keep this explicit instead of inheriting the conservative 4 KiB fallback
 * used by small secure-memory writes; otherwise large packages generate
 * roughly 16x more WebUSB transferOut calls.
 */
export const ADNL_FULL_PACKAGE_BLOCK_LEN = ADNL_USB_BLOCK_LEN;
/**
 * The ROM/SPL bootstrap path uses the small download blocks documented by
 * the public ADNL client. Keep this separate from the faster mwrite path:
 * BL1 and BL2 implementations are less tolerant of large WebUSB writes.
 */
export const ADNL_BOOTSTRAP_BLOCK_LEN = 16 * 1024;
/**
 * V3 protocol-6 CBW portions use 0x2000-byte native writes. Its download
 * command can answer OKAY before the raw payload is written.
 */
const ADNL_PROTOCOL6_BLOCK_LEN = 0x2000;
// The native V3 log waits for the BL2E gadget to disappear, then gives the
// new PID 0xC004 U-Boot gadget another two seconds before opening it. From
// the final BL2E CBW to identify stage 0x10 this is roughly six seconds.
const ADNL_PROTOCOL6_UBOOT_SETTLE_MS = 6_000;
const ADNL_RESPONSE_LEN = 4096;
const ANDROID_SPARSE_MAGIC = 0xed26ff3a;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

export interface AmlogicHandshakeInfo {
  mode: "ADNL";
  stage: "BL1" | "TPL" | "UBOOT";
  identify: string;
  identifyBytes: Uint8Array;
  secureBoot: boolean | "unknown";
}

export type AmlogicWipeMode = 0 | 1 | 3;

export interface AmlogicBurnOptions {
  experimental: boolean;
  allowBootloader: boolean;
  /** Skip the per-partition ADNL SHA-1 verification command. */
  skipVerify?: boolean;
  /** Send `oem disk_initial 0` before a standalone V3 partition write. */
  diskInitialZero?: boolean;
  wipe?: AmlogicWipeMode;
  /** Load BL2/TPL from an ADNL BootROM (BL1) session before mwrite. */
  bootstrapFromBl1?: boolean;
  /** Select the signed/unsigned boot chain when BL1 cannot report it. */
  bootstrapSecurity?: AmlogicBootstrapSecurity;
  /** Re-open the device after BL2 starts the U-Boot USB gadget. */
  reconnect?: () => Promise<void>;
  signal?: AbortSignal;
  /** Immutable preflight result shared by all selected target sessions. */
  imageReport?: AmlogicImageReport;
  onProgress?: (progress: OperationProgress) => void;
}

export type AmlogicBootstrapSecurity = "auto" | "normal" | "secure";

interface AdnlResponse {
  status: "OKAY" | "FAIL" | "INFO" | "DATA" | "DATAOUT";
  payload: Uint8Array;
  message?: string;
  dataSize?: number;
  fileOffset?: number;
}

type MWriteMode = "store" | "mem";
type MWriteFormat = "normal" | "sparse";

interface BurnItemOptions {
  blockLength?: number;
  mode?: MWriteMode;
  targetName?: string;
  format?: MWriteFormat;
  verify?: boolean;
}

export function validateAmlogicPacket(packet: Uint8Array, minimumLength = 4): void {
  if (packet.byteLength < minimumLength) {
    throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Packet ngắn hơn mức tối thiểu.");
  }
}

export function adnlChecksum(data: Uint8Array): number {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let sum = 0;
  const wordCount = Math.floor(data.byteLength / 4);
  for (let index = 0; index < wordCount; index += 1) sum = (sum + view.getUint32(index * 4, true)) >>> 0;
  const rest = data.byteLength % 4;
  if (rest) {
    let tail = 0;
    for (let index = 0; index < rest; index += 1) tail |= data[wordCount * 4 + index] << (index * 8);
    sum = (sum + tail) >>> 0;
  }
  return sum >>> 0;
}

export function isAdnlIdentifySignature(bytes: Uint8Array): boolean {
  return classifyAdnlIdentify(bytes) !== undefined;
}

export function isS4Bl1IdentifySignature(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 8 && bytes[0] === 6 && bytes[1] === 0 && bytes[2] === 0 && bytes[3] === 0 && bytes[5] === 0 && bytes[6] === 0 && bytes[7] === 0x0f;
}

export function isS4UbootIdentifySignature(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 8 && bytes[0] === 6 && bytes[1] === 0 && bytes[2] === 0 && bytes[3] === 0x10 && bytes[4] === 0 && bytes[5] === 0 && bytes[6] === 0 && bytes[7] === 0;
}

/** The V3 vendor flow uses identify protocol revision 6 for S4/T7 devices. */
export function isAdnlProtocol6Identify(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 4 && bytes[0] === 6;
}

function isS4StageIdentify(bytes: Uint8Array, stage: 8 | 12): boolean {
  return isAdnlProtocol6Identify(bytes) && bytes[1] === 0 && bytes[2] === 0 && bytes[3] === stage;
}

export function classifyAdnlIdentify(bytes: Uint8Array): "BL1" | "TPL" | "UBOOT" | undefined {
  if (isS4Bl1IdentifySignature(bytes)) return "BL1";
  // BL2/BL2E identify values are emitted while the ROM bootstrap is
  // transferring the U-Boot image. They are intentionally grouped under
  // TPL because the browser only exposes a single intermediate state.
  if (bytes.byteLength >= 8 && bytes[0] === 6 && bytes[1] === 0 && bytes[2] === 0 && (bytes[3] === 8 || bytes[3] === 12)) return "TPL";
  if (isS4UbootIdentifySignature(bytes)) return "UBOOT";
  if (bytes.byteLength >= ADNL_IDENTIFY_SIGNATURE.byteLength && ADNL_IDENTIFY_SIGNATURE.every((value, index) => bytes[index] === value)) return "UBOOT";
  return undefined;
}

export function parseAdnlResponse(packet: Uint8Array): AdnlResponse {
  validateAmlogicPacket(packet);
  const status = decoder.decode(packet.subarray(0, 4));
  if (status === "OKAY") return { status, payload: packet.subarray(4) };
  if (status === "FAIL") return { status, payload: packet.subarray(4), message: decoder.decode(packet.subarray(4)).replace(/\0+$/, "") };
  if (status === "INFO") {
    return { status, payload: packet.subarray(4), message: decoder.decode(packet.subarray(4)).replace(/\0+$/, "") };
  }

  const text = decoder.decode(packet).replace(/\0+$/, "");
  const match = text.match(/^DATAOUT(?::)?0x([0-9a-f]+):0x([0-9a-f]+)$/i);
  if (match) {
    return {
      status: "DATAOUT",
      payload: packet,
      dataSize: Number.parseInt(match[1], 16),
      fileOffset: Number.parseInt(match[2], 16),
    };
  }
  if (status === "DATA") return { status, payload: packet.subarray(4) };
  throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Phản hồi ADNL không hợp lệ: ${text.slice(0, 120)}`);
}

export interface AdnlControlBlockWord {
  sequence: number;
  size: number;
  offset: number;
  checksumDisabled: boolean;
  done: number;
}

/** Parse the 22-byte AMLC control block returned by getvar:cbw. */
export function parseAdnlControlBlockWord(payload: Uint8Array): AdnlControlBlockWord {
  if (payload.byteLength < 22 || decoder.decode(payload.subarray(0, 4)) !== "AMLC") {
    throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "CBW ADNL không hợp lệ khi nạp U-Boot từ BL1.");
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    sequence: view.getUint32(4, true),
    size: view.getUint32(8, true),
    offset: view.getUint32(12, true),
    checksumDisabled: payload[16] !== 0,
    done: payload[17],
  };
}

export function parseAdnlDownloadSize(payload: Uint8Array): number {
  const text = decoder.decode(payload).replace(/\0+$/, "").trim();
  const match = text.match(/(?:0x)?[0-9a-f]+/i);
  if (!match) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `ADNL trả downloadsize không hợp lệ: ${text.slice(0, 80)}`);
  const value = Number.parseInt(match[0], 16);
  if (!Number.isSafeInteger(value) || value <= 0) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Kích thước BL2 không hợp lệ: ${text.slice(0, 80)}`);
  return value;
}

function findUsbItem(items: AmlogicImageItem[], name: string): AmlogicImageItem | undefined {
  return items.find((item) => item.extension.toUpperCase() === "USB" && item.name.toUpperCase() === name.toUpperCase());
}

export function selectAdnlBootstrapItems(
  items: AmlogicImageItem[],
  security: AmlogicBootstrapSecurity = "auto",
): { ddr: AmlogicImageItem; uboot: AmlogicImageItem; secure: boolean } {
  const normalDdr = findUsbItem(items, "DDR");
  const secureDdr = findUsbItem(items, "DDR_ENC");
  const normalUboot = findUsbItem(items, "UBOOT");
  const secureUboot = findUsbItem(items, "UBOOT_ENC");
  const normal = normalDdr && normalUboot ? { ddr: normalDdr, uboot: normalUboot, secure: false } : undefined;
  const secure = secureDdr && secureUboot ? { ddr: secureDdr, uboot: secureUboot, secure: true } : undefined;

  if (security === "normal") {
    if (!normal) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Package BL1 không có đủ USB:DDR và USB:UBOOT không mã hóa.");
    return normal;
  }
  if (security === "secure") {
    if (!secure) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Package BL1 không có đủ USB:DDR_ENC và USB:UBOOT_ENC cho secure boot.");
    return secure;
  }
  if (normal && !secure) return normal;
  if (secure && !normal) return secure;
  if (normal && secure) throw new DeviceToolError("UNSUPPORTED_IMAGE", "BL1 không báo được secure boot và package có cả boot chain mã hóa/không mã hóa. Hãy chọn rõ chế độ Secure hoặc Non-secure.");
  throw new DeviceToolError("UNSUPPORTED_IMAGE", "Package BL1 cần item USB:DDR + USB:UBOOT (hoặc cặp _ENC tương ứng).");
}

/**
 * Map the human-friendly command shown by the UI to the wire command used by
 * the native ADNL client. Native `adnl reboot bootloader` queues the standard
 * fastboot command `reboot-bootloader`; it is not an OEM/U-Boot command and
 * secure U-Boots reject the OEM form with a whitelist error.
 */
export function normalizeAdnlUbootWireCommand(command: string): { command: string; disconnects: boolean } {
  const normalized = command.trim();
  if (/^reboot$/i.test(normalized)) return { command: "reboot", disconnects: true };
  const reboot = /^reboot(?:\s+|-)([a-z0-9_-]+)$/i.exec(normalized);
  if (reboot) return { command: `reboot-${reboot[1].toLowerCase()}`, disconnects: true };
  // `version` is exposed by the native client through the ADNL getvar path.
  // Sending it as `oem version` makes secure U-Boot report that the command
  // is not on its secure-boot whitelist.
  if (/^version$/i.test(normalized)) return { command: "getvar:version", disconnects: false };
  if (/^reset$/i.test(normalized)) return { command: "reboot", disconnects: true };
  const getvar = /^getvar\s+([^\s]+)$/i.exec(normalized);
  if (getvar) return { command: `getvar:${getvar[1]}`, disconnects: false };
  if (/^(?:oem|cmd)\s+/i.test(normalized)) {
    return { command: normalized.replace(/^cmd\s+/i, "oem "), disconnects: false };
  }
  return { command: `oem ${normalized}`, disconnects: false };
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
}

function linkedSignal(controller: AbortController, external?: AbortSignal): AbortSignal {
  if (!external) return controller.signal;
  if (external.aborted) controller.abort();
  else external.addEventListener("abort", () => controller.abort(), { once: true });
  return controller.signal;
}

function assertNotAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DeviceToolError("OPERATION_CANCELLED");
}

async function waitForProtocol6Loader(signal: AbortSignal, milliseconds = 550): Promise<void> {
  assertNotAborted(signal);
  await new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
  assertNotAborted(signal);
}

async function* readAdnlChunks(file: Blob, signal: AbortSignal, blockLength = ADNL_USB_BLOCK_LEN): AsyncGenerator<Uint8Array> {
  for (let offset = 0; offset < file.size; offset += ADNL_RAW_TRANSFER_LEN) {
    assertNotAborted(signal);
    const window = new Uint8Array(await readBlobArrayBuffer(file.slice(offset, Math.min(offset + ADNL_RAW_TRANSFER_LEN, file.size)), signal));
    for (let chunkOffset = 0; chunkOffset < window.byteLength; chunkOffset += blockLength) {
      assertNotAborted(signal);
      yield window.subarray(chunkOffset, Math.min(chunkOffset + blockLength, window.byteLength));
    }
  }
}

/**
 * Clean-room WebUSB ADNL client. It implements the public fastboot-like
 * command framing used by Amlogic ADNL; it does not bundle vendor binaries
 * or attempt to bypass secure boot.
 */
export class AmlogicProtocol {
  private currentState: AmlogicState = "DISCONNECTED";
  private operationController?: AbortController;
  private handshakeResult?: AmlogicHandshakeInfo;

  constructor(private readonly transport: WebUsbTransport) {}

  get state(): AmlogicState {
    return this.currentState;
  }

  get handshakeInfo(): AmlogicHandshakeInfo | undefined {
    return this.handshakeResult;
  }

  /** True while an active burn is moving from BL1/BL2/BL2E to U-Boot. */
  get isBootstrapping(): boolean {
    return Boolean(this.operationController) && this.handshakeResult?.stage !== "UBOOT";
  }

  markDisconnected(): void {
    this.handshakeResult = undefined;
    this.currentState = "DISCONNECTED";
  }

  async handshake(signal?: AbortSignal): Promise<AmlogicHandshakeInfo> {
    if (!this.transport.isConnected()) throw new DeviceToolError("DEVICE_DISCONNECTED");
    this.currentState = "HANDSHAKING";
    try {
      const identifyResponse = await this.command("getvar:identify", signal);
      const stage = identifyResponse.status === "OKAY" ? classifyAdnlIdentify(identifyResponse.payload) : undefined;
      if (!stage) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Thiết bị không trả về signature ADNL S4/S905W2 đã biết (nhận ${bytesToHex(identifyResponse.payload)}).`);
      }

      // Loader responses can include diagnostic words after the 8-byte
      // identify record. Stage decisions and reconnect diagnostics must use
      // only the fixed identify header, as the native V3 tool does.
      const actualIdentify = identifyResponse.payload.slice(0, 8);
      if (stage !== "UBOOT") {
        this.handshakeResult = { mode: "ADNL", stage, identify: bytesToHex(actualIdentify), identifyBytes: actualIdentify, secureBoot: "unknown" };
        this.currentState = "READY";
        return this.handshakeResult;
      }

      if (isS4UbootIdentifySignature(actualIdentify)) {
        const secureBoot = await this.readSecureBoot(signal);
        this.handshakeResult = { mode: "ADNL", stage, identify: bytesToHex(actualIdentify), identifyBytes: actualIdentify, secureBoot };
        this.currentState = "READY";
        return this.handshakeResult;
      }

      await this.expectOkay("oem setvar burnsteps 1", signal);
      await this.expectOkay("oem disk_initial 0", signal);
      const secureBoot = await this.readSecureBoot(signal);
      if (secureBoot === "unknown") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Không đọc được trạng thái secure boot.");
      this.handshakeResult = { mode: "ADNL", stage, identify: bytesToHex(actualIdentify), identifyBytes: actualIdentify, secureBoot };
      this.currentState = "READY";
      return this.handshakeResult;
    } catch (error) {
      this.currentState = signal?.aborted ? "CANCELLED" : "FAILED";
      if (error instanceof DeviceToolError) throw error;
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", error instanceof Error ? error.message : String(error), undefined, error);
    }
  }

  async burn(file: File, options: AmlogicBurnOptions): Promise<void> {
    if (!ADNL_PROTOCOL_IMPLEMENTED) throw new DeviceToolError("UNSUPPORTED_IMAGE");
    if (!options.experimental) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Hãy bật chế độ ADNL thử nghiệm trước khi ghi.");
    if (!this.handshakeResult) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Chưa handshake ADNL với thiết bị.");
    const controller = new AbortController();
    this.operationController = controller;
    const signal = linkedSignal(controller, options.signal);
    try {
      this.currentState = "PREPARING_IMAGE";
      const report = options.imageReport ?? await parseAmlogicImage(file);
      if (this.handshakeResult.stage === "BL1") {
        if (!options.bootstrapFromBl1) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Thiết bị đang ở ADNL BL1. Hãy bật luồng nạp BL2/U-Boot từ BL1.");
        if (!options.reconnect) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Không có callback mở lại thiết bị sau khi BL2 chuyển sang U-Boot.");
        await this.bootstrapFromBl1(file, report.items, signal, options);
        await this.reconnectToUboot(options.reconnect, signal);
      }
      if (this.handshakeResult.stage !== "UBOOT") throw new DeviceToolError("UNSUPPORTED_IMAGE", `Thiết bị đang ở ADNL ${this.handshakeResult.stage}. Cần U-Boot để chạy mwrite.`);
      const burnItems = report.items
        .filter((item) => item.extension.toUpperCase() === "PARTITION")
        // The vendor V3 flow always commits the bootloader last. Keeping it
        // at the end prevents a bootloader payload from changing the running
        // media state before the remaining partitions are verified.
        .sort((left, right) => Number(isBootloaderPartition(left.name)) - Number(isBootloaderPartition(right.name)));
      if (burnItems.length === 0) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Package không có item PARTITION để ghi qua ADNL.");
      const sensitiveItems = burnItems.filter((item) => isSensitivePartition(item.name));
      if (sensitiveItems.length > 0 && !options.allowBootloader) {
        throw new DeviceToolError("UNSUPPORTED_IMAGE", `Package chứa phân vùng rủi ro cao: ${sensitiveItems.map((item) => item.name).join(", ")}. Hãy bật tùy chọn bootloader/TPL để tiếp tục.`);
      }

      await this.prepareFlash(file, report.items, signal, options);

      const totalBytes = burnItems.reduce((total, item) => total + item.size, 0);
      let completedBytes = 0;
      const startedAt = Date.now();
      for (const item of burnItems) {
        assertNotAborted(signal);
      await this.burnItem(file, item, signal, (value) => {
          options.onProgress?.({
            phase: `ADNL · ${item.name}`,
            completedBytes: completedBytes + value,
            totalBytes,
            startedAt,
            speedBytesPerSecond: (completedBytes + value) / Math.max(0.001, (Date.now() - startedAt) / 1000),
          });
        }, {
          blockLength: ADNL_FULL_PACKAGE_BLOCK_LEN,
          format: await this.detectMWriteFormat(file, item, signal),
          verify: !options.skipVerify,
        });
        completedBytes += item.size;
      }
      this.currentState = "FINALIZING";
      await this.expectOkay("oem save_setting", signal);
      this.currentState = "COMPLETED";
    } catch (error) {
      this.currentState = signal.aborted || error instanceof DeviceToolError && error.code === "OPERATION_CANCELLED" ? "CANCELLED" : "FAILED";
      if (error instanceof DeviceToolError) throw error;
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", error instanceof Error ? error.message : String(error), undefined, error);
    } finally {
      this.operationController = undefined;
    }
  }

  /**
   * Bootstrap the ADNL ROM path described by the public client:
   * BL1/ROM -> BL2/BL2E -> U-Boot. The final U-Boot gadget is re-enumerated
   * by the service before the regular mwrite flow starts.
   */
  private async bootstrapFromBl1(
    file: File,
    items: AmlogicImageItem[],
    signal: AbortSignal,
    options: AmlogicBurnOptions,
  ): Promise<void> {
    const selected = selectAdnlBootstrapItems(items, options.bootstrapSecurity ?? "auto");
    const startedAt = Date.now();
    const report = (phase: string, completedBytes = 0, totalBytes?: number) => {
      options.onProgress?.({ phase, completedBytes, totalBytes, startedAt });
    };
    report(`ADNL · BL1 · ${selected.secure ? "secure" : "non-secure"} boot chain`);

    // S4/T7 V3 devices identify as protocol revision 6. Their vendor flow
    // does not expose the public ADNL burnsteps DATA handshake; it goes from
    // chip-info directly to downloadsize, then re-enumerates at BL2/BL2E.
    // Sending the protocol-5 burnsteps command here makes BL1 reject the
    // session before any image bytes are accepted.
    if (isAdnlProtocol6Identify(this.handshakeResult?.identifyBytes ?? new Uint8Array())) {
      await this.bootstrapProtocol6(file, selected, signal, options, report);
      return;
    }

    // These information reads are part of the ROM flow used by the vendor
    // client. They are best-effort because older S4 ROM revisions omit one
    // or more chip-info pages; downloadsize remains the required probe.
    for (const command of [
      "getvar:serialno",
      "getvar:getchipinfo-1",
      "getvar:getchipinfo-0",
      "getvar:getchipinfo-1",
      "getvar:getchipinfo-2",
      "getvar:getchipinfo-3",
    ]) {
      await this.command(command, signal).catch(() => undefined);
    }

    await this.setBootromBurnSteps(0xc0040000, signal);
    await this.command("getvar:getchipinfo-1", signal).catch(() => undefined);
    await this.setBootromBurnSteps(0xc0040001, signal);

    const downloadSizeResponse = await this.command("getvar:downloadsize", signal);
    if (downloadSizeResponse.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "BL1 không trả downloadsize cho BL2.");
    const bl2Size = parseAdnlDownloadSize(downloadSizeResponse.payload);
    if (bl2Size > selected.ddr.size) throw new DeviceToolError("UNSUPPORTED_IMAGE", `BL1 yêu cầu ${formatHex(bl2Size)} byte BL2 nhưng item ${selected.ddr.name} chỉ có ${formatHex(selected.ddr.size)} byte.`);

    report("ADNL · BL1 · nạp BL2", 0, bl2Size);
    await this.expectData(`download:${bl2Size.toString(16).padStart(8, "0")}`, signal);
    await this.transferFileRange(file, selected.ddr, 0, bl2Size, signal, (completed) => report("ADNL · BL1 · nạp BL2", completed, bl2Size));
    await this.expectOkayAfterRaw(signal, "BL1 không xác nhận dữ liệu BL2.");
    await this.setBootromBurnSteps(0xc0040002, signal);
    await this.expectOkay("boot", signal);

    // BL2 now requests the TPL/U-Boot image through the AMLC CBW records.
    const bl2Identify = await this.command("getvar:identify", signal);
    const bl2Stage = bl2Identify.status === "OKAY" ? classifyAdnlIdentify(bl2Identify.payload) : undefined;
    if (bl2Stage !== "TPL") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `BL2 không sẵn sàng sau khi boot (nhận ${bl2Identify.status}).`);
    await this.setBootromBurnSteps(0xc0040003, signal);
    let transferred = 0;
    report("ADNL · BL2/BL2E · chờ CBW U-Boot", 0, selected.uboot.size);
    for (;;) {
      assertNotAborted(signal);
      const cbwResponse = await this.command("getvar:cbw", signal);
      if (cbwResponse.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "BL2 không trả CBW để nạp U-Boot.");
      const cbw = parseAdnlControlBlockWord(cbwResponse.payload);
      if (cbw.done !== 0) break;
      if (cbw.size <= 0 || cbw.offset < 0 || cbw.offset + cbw.size > selected.uboot.size) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `CBW U-Boot vượt bounds (offset=${formatHex(cbw.offset)}, size=${formatHex(cbw.size)}).`);
      }
      let remaining = cbw.size;
      let offset = cbw.offset;
      let checksum = 0;
      while (remaining > 0) {
        const chunkLength = Math.min(remaining, ADNL_PROTOCOL6_BLOCK_LEN);
        const chunk = new Uint8Array(await readBlobArrayBuffer(file.slice(selected.uboot.offset + offset, selected.uboot.offset + offset + chunkLength), signal));
        if (chunk.byteLength !== chunkLength) throw new DeviceToolError("INVALID_IMAGE", "Không đọc đủ dữ liệu U-Boot từ package.");
        checksum = (checksum + adnlChecksum(chunk)) >>> 0;
        await this.transferProtocol6Payload(`download:${chunkLength.toString(16).padStart(8, "0")}`, chunk, signal, "BL2 không chấp nhận dữ liệu U-Boot.");
        offset += chunkLength;
        remaining -= chunkLength;
        transferred = Math.max(transferred, offset);
        report("ADNL · BL2/BL2E · nạp U-Boot", transferred, selected.uboot.size);
      }
      await this.setProtocol6Checksum(checksum, signal, "BL2 từ chối checksum portion U-Boot.");
    }
    report("ADNL · BL2/BL2E · U-Boot đã nạp; chờ thiết bị re-enumerate", selected.uboot.size, selected.uboot.size);
  }

  private async reconnectToUboot(reconnect: () => Promise<void>, signal: AbortSignal): Promise<void> {
    const maxAttempts = 4;
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      assertNotAborted(signal);
      try {
        await reconnect();
        // Allow endpoint descriptors and the ADNL handler to settle after
        // Chrome opens the newly enumerated gadget.
        await waitForProtocol6Loader(signal, 200);
        const handshake = await this.handshake(signal);
        if (handshake.stage === "UBOOT") return;
        lastError = new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Lần kết nối lại ${attempt}/${maxAttempts} vẫn nhận stage ${handshake.stage}.`);
      } catch (error) {
        if (signal.aborted || error instanceof DeviceToolError && error.code === "OPERATION_CANCELLED") throw error;
        lastError = error;
      }
      if (attempt < maxAttempts) await waitForProtocol6Loader(signal, 750);
    }
    const detail = lastError instanceof Error ? lastError.message : String(lastError ?? "không có phản hồi");
    throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Không mở được U-Boot stage 0x10 sau ${maxAttempts} lần tự động thử lại. ${detail}`, undefined, lastError);
  }

  /**
   * Vendor V3 protocol-6 flow observed on S4/T7 BL1 devices:
   * ROM -> BL2 -> BL2E -> U-Boot. The BL2 and BL2E stages each expose their
   * own CBW sequence and disappear from USB after the final CBW.
   */
  private async bootstrapProtocol6(
    file: File,
    selected: { ddr: AmlogicImageItem; uboot: AmlogicImageItem; secure: boolean },
    signal: AbortSignal,
    options: AmlogicBurnOptions,
    report: (phase: string, completedBytes?: number, totalBytes?: number) => void,
  ): Promise<void> {
    for (const command of [
      "getvar:serialno",
      "getvar:getchipinfo-1",
      "getvar:getchipinfo-0",
      "getvar:getchipinfo-1",
      "getvar:getchipinfo-2",
      "getvar:getchipinfo-3",
    ]) {
      await this.command(command, signal).catch(() => undefined);
    }

    const downloadSizeResponse = await this.command("getvar:downloadsize", signal);
    if (downloadSizeResponse.status !== "OKAY") {
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(downloadSizeResponse, "BL1 không trả downloadsize cho BL2."));
    }
    const bl2Size = parseAdnlDownloadSize(downloadSizeResponse.payload);
    if (bl2Size > selected.ddr.size) {
      throw new DeviceToolError("UNSUPPORTED_IMAGE", `BL1 yêu cầu ${formatHex(bl2Size)} byte BL2 nhưng item ${selected.ddr.name} chỉ có ${formatHex(selected.ddr.size)} byte.`);
    }

    report("ADNL · BL1 · nạp BL2", 0, bl2Size);
    await this.expectData(`download:${bl2Size.toString(16).padStart(8, "0")}`, signal);
    await this.transferFileRange(file, selected.ddr, 0, bl2Size, signal, (completed) => report("ADNL · BL1 · nạp BL2", completed, bl2Size));
    await this.expectOkayAfterRaw(signal, "BL1 không xác nhận dữ liệu BL2.");
    await this.expectOkay("boot", signal);

    // BL1 tears down the ROM gadget and exposes BL2 as a new USB device.
    await options.reconnect?.();
    let loader = await this.handshake(signal);
    const maxBl2Passes = 4;
    let bl2Pass = 0;
    if (!isS4StageIdentify(loader.identifyBytes, 8)) {
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `BL2 không sẵn sàng sau khi boot (nhận ${loader.identify}).`);
    }

    // Protocol 6 reports CBW done=1 after the first BL2 portion, waits about
    // 500 ms, then identifies as stage 08 again and requests the next CBW.
    // Only the last BL2 pass re-enumerates as stage 0c (BL2E).
    while (isS4StageIdentify(loader.identifyBytes, 8)) {
      bl2Pass += 1;
      if (bl2Pass > maxBl2Passes) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `BL2 vẫn ở stage 08 sau ${maxBl2Passes} lượt CBW; đã dừng để tránh lặp vô hạn.`);
      }
      await this.transferProtocol6CbwStage(file, selected.uboot, signal, report, "BL2");
      await waitForProtocol6Loader(signal);
      await options.reconnect?.();
      loader = await this.handshake(signal);
    }

    if (!isS4StageIdentify(loader.identifyBytes, 12)) {
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `BL2E không sẵn sàng sau khi BL2 kết thúc (nhận ${loader.identify}).`);
    }
    const bl2eTransferred = await this.transferProtocol6CbwStage(file, selected.uboot, signal, report, "BL2E");
    report("ADNL · BL2E · chuyển sang U-Boot; đang tự động kết nối lại", bl2eTransferred, selected.uboot.size);
    await waitForProtocol6Loader(signal, ADNL_PROTOCOL6_UBOOT_SETTLE_MS);
  }

  private async transferProtocol6CbwStage(
    file: File,
    uboot: AmlogicImageItem,
    signal: AbortSignal,
    report: (phase: string, completedBytes?: number, totalBytes?: number) => void,
    stageName: "BL2" | "BL2E",
  ): Promise<number> {
    let transferred = 0;
    report(`ADNL · ${stageName} · chờ CBW U-Boot`, 0, uboot.size);
    for (;;) {
      assertNotAborted(signal);
      let cbwResponse: AdnlResponse;
      try {
        cbwResponse = await this.command("getvar:cbw", signal);
      } catch (error) {
        // BL2E disconnects its USB gadget immediately after the final image
        // range. Chromium reports that expected re-enumeration as a stalled
        // transferIn because there is no final CBW frame to read. The native
        // V3 flow treats the same disconnect as the signal to reacquire the
        // stage-10 U-Boot gadget.
        const finalRangeComplete = stageName === "BL2E"
          && transferred > 0
          && uboot.size - transferred <= ADNL_PROTOCOL6_BLOCK_LEN;
        const expectedTransition = error instanceof DeviceToolError
          && ["TRANSFER_STALL", "TRANSFER_TIMEOUT", "DEVICE_DISCONNECTED"].includes(error.code);
        if (finalRangeComplete && expectedTransition) {
          return transferred;
        }
        throw error;
      }
      if (cbwResponse.status !== "OKAY") {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(cbwResponse, `${stageName} không trả CBW để nạp U-Boot.`));
      }
      const cbw = parseAdnlControlBlockWord(cbwResponse.payload);
      if (cbw.done !== 0) break;
      if (cbw.size <= 0 || cbw.offset < 0 || cbw.offset + cbw.size > uboot.size) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `CBW ${stageName} vượt bounds (offset=${formatHex(cbw.offset)}, size=${formatHex(cbw.size)}).`);
      }

      let remaining = cbw.size;
      let offset = cbw.offset;
      let checksum = 0;
      while (remaining > 0) {
        const chunkLength = Math.min(remaining, ADNL_PROTOCOL6_BLOCK_LEN);
        const chunk = new Uint8Array(await readBlobArrayBuffer(file.slice(uboot.offset + offset, uboot.offset + offset + chunkLength), signal));
        if (chunk.byteLength !== chunkLength) throw new DeviceToolError("INVALID_IMAGE", "Không đọc đủ dữ liệu U-Boot từ package.");
        checksum = (checksum + adnlChecksum(chunk)) >>> 0;
        await this.transferProtocol6Payload(
          `download:${chunkLength.toString(16).padStart(8, "0")}`,
          chunk,
          signal,
          `${stageName} không chấp nhận dữ liệu U-Boot.`,
        );
        offset += chunkLength;
        remaining -= chunkLength;
        transferred = Math.max(transferred, offset);
        report(`ADNL · ${stageName} · nạp U-Boot`, transferred, uboot.size);
      }
      if (!cbw.checksumDisabled) {
        await this.setProtocol6Checksum(checksum, signal, `${stageName} từ chối checksum portion U-Boot.`);
      }
    }
    report(`ADNL · ${stageName} · U-Boot portion đã nạp`, transferred, uboot.size);
    return transferred;
  }

  async burnPartition(file: File, partitionName: string, options: AmlogicBurnOptions): Promise<void> {
    if (!ADNL_PROTOCOL_IMPLEMENTED) throw new DeviceToolError("UNSUPPORTED_IMAGE");
    if (!options.experimental) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Chế độ ADNL chưa được bật trong adapter.");
    if (!this.handshakeResult) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Chưa handshake ADNL với thiết bị.");
    if (this.handshakeResult.stage !== "UBOOT") throw new DeviceToolError("UNSUPPORTED_IMAGE", `Thiết bị đang ở ADNL ${this.handshakeResult.stage}. Cần U-Boot trước khi ghi partition.`);
    if (!file.size) throw new DeviceToolError("INVALID_IMAGE", "File partition rỗng.");
    if (isSensitivePartition(partitionName) && !options.allowBootloader) {
      throw new DeviceToolError("UNSUPPORTED_IMAGE", `Partition ${partitionName} là vùng rủi ro cao. Hãy bật tùy chọn bootloader/TPL để tiếp tục.`);
    }

    const controller = new AbortController();
    this.operationController = controller;
    const signal = linkedSignal(controller, options.signal);
    const startedAt = Date.now();
    try {
      this.currentState = "PREPARING_IMAGE";
      if (options.diskInitialZero) await this.expectOkay("oem disk_initial 0", signal);
      const format = await this.detectMWriteFormat(file, { offset: 0, size: file.size }, signal);
      await this.burnItem(file, { offset: 0, size: file.size, name: partitionName }, signal, (value) => {
        options.onProgress?.({
          phase: `ADNL · ${partitionName}`,
          completedBytes: value,
          totalBytes: file.size,
          startedAt,
          speedBytesPerSecond: value / Math.max(0.001, (Date.now() - startedAt) / 1000),
        });
      }, { blockLength: ADNL_USB_BLOCK_LEN, format, verify: !options.skipVerify });
      this.currentState = "COMPLETED";
    } catch (error) {
      this.currentState = signal.aborted || error instanceof DeviceToolError && error.code === "OPERATION_CANCELLED" ? "CANCELLED" : "FAILED";
      if (error instanceof DeviceToolError) throw error;
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", error instanceof Error ? error.message : String(error), undefined, error);
    } finally {
      this.operationController = undefined;
    }
  }

  async cancelCurrentOperation(): Promise<void> {
    this.operationController?.abort();
    await this.transport.cancelCurrentOperation();
  }

  async runUbootCommand(command: string): Promise<string> {
    if (!this.handshakeResult) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Chưa handshake ADNL với thiết bị.");
    if (this.handshakeResult.stage !== "UBOOT") throw new DeviceToolError("UNSUPPORTED_IMAGE", `U-Boot command cần thiết bị ở stage UBOOT; hiện tại là ${this.handshakeResult.stage}.`);

    const controller = new AbortController();
    this.operationController = controller;
    const infoOutput: string[] = [];
    try {
      const wire = normalizeAdnlUbootWireCommand(command);
      let response: AdnlResponse;
      if (wire.disconnects) {
        // The native client waits for the USB gadget to disappear after a
        // reboot command. Some U-Boot builds send no terminal response, so
        // accept a transport drop after the command has been written.
        await this.transport.transferOut(encoder.encode(wire.command), { signal: controller.signal, timeoutMs: 15_000 });
        try {
          response = await this.readResponse(controller.signal, (message) => {
            if (message) infoOutput.push(message);
          });
        } catch (error) {
          if (!this.transport.isConnected() || error instanceof DeviceToolError && ["DEVICE_DISCONNECTED", "TRANSFER_STALL", "TRANSFER_TIMEOUT"].includes(error.code)) {
            return [...infoOutput, `Đã gửi ${wire.command}; thiết bị đang khởi động lại.`].join("\n");
          }
          throw error;
        }
      } else {
        response = await this.command(wire.command, controller.signal, (message) => {
          if (message) infoOutput.push(message);
        });
      }
      if (response.status === "FAIL") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", response.message ?? `U-Boot từ chối lệnh ${command}.`);
      if (response.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `U-Boot không trả OKAY cho lệnh ${command}.`);
      const terminalOutput = decoder.decode(response.payload).replace(/\0+$/, "").trim();
      return [...infoOutput, terminalOutput].filter(Boolean).join("\n") || "Lệnh đã chạy thành công; firmware không trả output qua ADNL.";
    } finally {
      this.operationController = undefined;
    }
  }

  private async setBootromBurnSteps(value: number, signal: AbortSignal): Promise<void> {
    const response = await this.command("setvar:burnsteps", signal);
    if (response.status !== "DATA") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(response, `BL1 không mở payload burnsteps ${formatHex(value)}.`));
    const payload = new Uint8Array(4);
    new DataView(payload.buffer).setUint32(0, value >>> 0, true);
    await this.transport.transferOut(payload, { signal });
    await this.expectOkayAfterRaw(signal, `BL1 không xác nhận burnsteps ${formatHex(value)}.`);
  }

  private async setChecksum(value: number, signal: AbortSignal): Promise<void> {
    const response = await this.command("setvar:checksum", signal);
    if (response.status !== "DATA") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(response, "BL2 không yêu cầu checksum cho portion U-Boot."));
    const payload = new Uint8Array(4);
    new DataView(payload.buffer).setUint32(0, value >>> 0, true);
    await this.transport.transferOut(payload, { signal });
    await this.expectOkayAfterRaw(signal, "BL2 từ chối checksum portion U-Boot.");
  }

  /**
   * Protocol 6's native DLL sends the command and its raw data as one logical
   * operation. The first response is commonly OKAY with an empty payload; it
   * only opens the raw-data phase. A terminal OKAY/FAIL follows the payload.
   */
  private async transferProtocol6Payload(command: string, payload: Uint8Array, signal: AbortSignal, detail: string): Promise<void> {
    const response = await this.command(command, signal);
    if (response.status !== "DATA" && response.status !== "OKAY") {
      throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(response, detail));
    }
    await this.transport.transferOut(payload, { signal, timeoutMs: 30_000 });
    await this.expectOkayAfterRaw(signal, detail);
  }

  private async setProtocol6Checksum(value: number, signal: AbortSignal, detail: string): Promise<void> {
    const payload = new Uint8Array(4);
    new DataView(payload.buffer).setUint32(0, value >>> 0, true);
    await this.transferProtocol6Payload("setvar:checksum", payload, signal, detail);
  }

  private async expectData(command: string, signal: AbortSignal): Promise<AdnlResponse> {
    const response = await this.command(command, signal);
    if (response.status !== "DATA") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", this.describeAdnlResponse(response, `Thiết bị không mở kênh dữ liệu cho ${command}.`));
    return response;
  }

  private describeAdnlResponse(response: AdnlResponse, fallback: string): string {
    const payload = bytesToHex(response.payload).slice(0, 96);
    const message = response.message ? `, message=${response.message}` : "";
    return `${fallback} (response=${response.status}, payload=${payload || "<empty>"}${message})`;
  }

  private async expectOkayAfterRaw(signal: AbortSignal, detail: string): Promise<void> {
    const response = await this.readResponse(signal, undefined, { ignoreEmpty: true });
    if (response.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", response.message ?? detail);
  }

  private async transferFileRange(
    file: Blob,
    item: Pick<AmlogicImageItem, "offset">,
    itemOffset: number,
    length: number,
    signal: AbortSignal,
    onBytes: (bytes: number) => void,
  ): Promise<void> {
    let completed = 0;
    while (completed < length) {
      assertNotAborted(signal);
      const chunkLength = Math.min(ADNL_BOOTSTRAP_BLOCK_LEN, length - completed);
      const chunk = new Uint8Array(await readBlobArrayBuffer(file.slice(item.offset + itemOffset + completed, item.offset + itemOffset + completed + chunkLength), signal));
      if (chunk.byteLength !== chunkLength) throw new DeviceToolError("INVALID_IMAGE", "Không đọc đủ dữ liệu BL2 từ package.");
      await this.transport.transferOut(chunk, { signal, timeoutMs: 30_000 });
      completed += chunk.byteLength;
      onBytes(completed);
    }
  }

  private async readSecureBoot(signal?: AbortSignal): Promise<boolean | "unknown"> {
    try {
      const response = await this.command("getvar:secureboot", signal);
      if (response.status !== "OKAY") return "unknown";
      const text = decoder.decode(response.payload).replace(/\0/g, "").trim().toLowerCase();
      if (/\b(enabled|enable|true|yes|on)\b/.test(text) || /(?:^|[:=\s])1(?:$|\s)/.test(text)) return true;
      if (/\b(disabled|disable|false|no|off)\b/.test(text) || /(?:^|[:=\s])0(?:$|\s)/.test(text)) return false;
      if (response.payload.byteLength < 4) return "unknown";
      return new DataView(response.payload.buffer, response.payload.byteOffset, response.payload.byteLength).getUint32(0, true) !== 0;
    } catch {
      return "unknown";
    }
  }

  private async burnItem(
    file: File,
    item: Pick<AmlogicImageItem, "offset" | "size" | "name">,
    signal: AbortSignal,
    onBytes: (bytes: number) => void,
    blockLengthOrOptions: number | BurnItemOptions | undefined = this.handshakeResult?.secureBoot === false ? ADNL_USB_BLOCK_LEN : ADNL_COMPAT_USB_BLOCK_LEN,
    options: BurnItemOptions = {},
  ): Promise<void> {
    const itemOptions: BurnItemOptions = typeof blockLengthOrOptions === "number"
      ? { ...options, blockLength: blockLengthOrOptions }
      : { ...options, ...(blockLengthOrOptions ?? {}) };
    const mode = itemOptions.mode ?? "store";
    const targetName = itemOptions.targetName ?? item.name;
    const format = mode === "mem" ? "normal" : itemOptions.format ?? "normal";
    const blockLength = itemOptions.blockLength ?? (this.handshakeResult?.secureBoot === false ? ADNL_USB_BLOCK_LEN : ADNL_COMPAT_USB_BLOCK_LEN);
    this.currentState = "ERASING";
    // Keep the vendor ADNL spelling: hexadecimal size plus an explicit
    // zero partition offset. Some secure-boot U-Boot builds accept the
    // shorter form but fail on the next portion when the offset is omitted.
    const mwriteCommand = mode === "mem"
      ? `oem mwrite 0x${item.size.toString(16)} ${format} mem ${targetName}`
      : `oem mwrite 0x${item.size.toString(16)} ${format} store ${targetName} 0x0`;
    await this.expectOkay(mwriteCommand, signal);
    const hasher = new SHA1();
    let processed = 0;
    for (;;) {
      assertNotAborted(signal);
      this.currentState = "DOWNLOADING";
      const response = await this.command("mwrite:verify=addsum", signal);
      if (response.status === "OKAY") break;
      if (response.status !== "DATAOUT" || response.dataSize === undefined || response.fileOffset === undefined) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `ADNL không yêu cầu dữ liệu cho partition ${item.name}.`);
      }
      const dataSize = response.dataSize;
      const fileOffset = response.fileOffset;
      if (dataSize <= 0 || dataSize > item.size || fileOffset < 0 || fileOffset + dataSize > item.size) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `DATAOUT vượt bounds tại partition ${item.name}.`);
      }
      if (fileOffset !== processed) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `ADNL trả offset không tuần tự (${fileOffset}, cần ${processed}).`);
      }
      const portion = file.slice(item.offset + fileOffset, item.offset + fileOffset + dataSize);
      let checksum = 0;
      for await (const chunk of readAdnlChunks(portion, signal, blockLength)) {
        assertNotAborted(signal);
        checksum = (checksum + adnlChecksum(chunk)) >>> 0;
        hasher.update(chunk);
        await this.transport.transferOut(chunk, { signal });
        processed += chunk.byteLength;
        onBytes(processed);
      }
      const checksumBytes = new Uint8Array(4);
      new DataView(checksumBytes.buffer).setUint32(0, checksum >>> 0, true);
      await this.transport.transferOut(checksumBytes, { signal });
      // ADNL may emit a zero-length IN packet after the raw portion and
      // before the terminal OKAY/FAIL response. It is a USB sync packet, not
      // an ADNL command response; do not feed it to the 4-byte parser.
      const completion = await this.readResponse(signal, undefined, { ignoreEmpty: true });
      if (completion.status === "FAIL") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", completion.message ?? "Thiết bị từ chối checksum hoặc ghi partition.");
      if (completion.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Thiết bị không xác nhận portion ADNL.");
    }
    if (processed !== item.size) throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Thiếu dữ liệu partition ${item.name}.`);
    if (itemOptions.verify === false) return;
    this.currentState = "VERIFYING";
    const digest = bytesToHex(hasher.digest());
    await this.expectOkay(`oem verify sha1sum ${digest}`, signal);
  }

  private async prepareFlash(
    file: File,
    items: AmlogicImageItem[],
    signal: AbortSignal,
    options: AmlogicBurnOptions,
  ): Promise<void> {
    const dtb = items.find((item) => item.extension.toUpperCase() === "DTB");
    const bootloader = items.find((item) => item.extension.toUpperCase() === "PARTITION" && item.name.toLowerCase() === "bootloader");
    const secureBoot = this.handshakeResult?.secureBoot === true;

    this.currentState = "ERASING";
    await this.expectOkay("oem setvar burnsteps 0xc0041030", signal);

    if (secureBoot) {
      if (!dtb) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Package secure boot không có item DTB để nạp vào mem dtb.");
      await this.burnItem(file, dtb, signal, () => undefined, {
        mode: "mem",
        targetName: "dtb",
        verify: false,
      });

      const sheader = await this.command("oem sheader_need", signal);
      if (sheader.status === "OKAY") {
        if (!bootloader) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Thiết bị yêu cầu sheader nhưng package không có PARTITION bootloader.");
        await this.burnItem(file, bootloader, signal, () => undefined, {
          mode: "mem",
          targetName: "sheader",
          verify: false,
        });
      } else if (sheader.status !== "FAIL" || !isOptionalSheaderFailure(sheader.message)) {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", sheader.message ?? "Thiết bị không trả lời hợp lệ cho sheader_need.");
      }

      // Secure U-Boots commonly reject this probe because it is not on the
      // secure whitelist. The vendor tool logs the failure and continues.
      const bootloaderVersion = await this.command("oem get_bootloaderversion", signal);
      if (bootloaderVersion.status !== "OKAY" && bootloaderVersion.status !== "FAIL") {
        throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", "Thiết bị không trả lời cho get_bootloaderversion.");
      }

      await this.expectOkay("oem setvar burnsteps 0xc0041031", signal);
      await this.burnItem(file, dtb, signal, () => undefined, {
        mode: "mem",
        targetName: "dtb",
        verify: false,
      });
      await this.expectOkay(`oem disk_initial ${options.wipe ?? 1}`, signal);
      await this.expectOkay("oem setvar burnsteps 0xc0041032", signal);
      return;
    }

    await this.expectOkay("oem setvar burnsteps 0xc0041031", signal);
    await this.expectOkay(`oem disk_initial ${options.wipe ?? 0}`, signal);
    await this.expectOkay("oem setvar burnsteps 0xc0041032", signal);
  }

  private async detectMWriteFormat(file: File, item: { offset: number; size: number; fileType?: number }, signal?: AbortSignal): Promise<MWriteFormat> {
    if (item.fileType === 0xfe) return "sparse";
    const header = new Uint8Array(await readBlobArrayBuffer(file.slice(item.offset, Math.min(item.offset + 4, file.size)), signal));
    if (header.byteLength < 4) return "normal";
    return new DataView(header.buffer, header.byteOffset, header.byteLength).getUint32(0, true) === ANDROID_SPARSE_MAGIC ? "sparse" : "normal";
  }

  private async expectOkay(command: string, signal?: AbortSignal): Promise<AdnlResponse> {
    const response = await this.command(command, signal);
    if (response.status === "FAIL") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", response.message ?? `Thiết bị từ chối lệnh ${command}.`);
    if (response.status !== "OKAY") throw new DeviceToolError("AMLOGIC_HANDSHAKE_FAILED", `Thiết bị không trả OKAY cho lệnh ${command}.`);
    return response;
  }

  private async command(command: string, signal?: AbortSignal, onInfo?: (message: string) => void): Promise<AdnlResponse> {
    const transferOptions: TransferOptions = { signal, timeoutMs: 15_000 };
    await this.transport.transferOut(encoder.encode(command), transferOptions);
    return this.readResponse(signal, onInfo);
  }

  private async readResponse(signal?: AbortSignal, onInfo?: (message: string) => void, options: { ignoreEmpty?: boolean } = {}): Promise<AdnlResponse> {
    for (;;) {
      const packet = await this.transport.transferIn(ADNL_RESPONSE_LEN, { signal, timeoutMs: 15_000 });
      // Amlogic ADNL can place a zero-length USB IN synchronization packet
      // before a command's terminal response. There is no payload to parse;
      // wait for the following OKAY/FAIL/DATAOUT frame. Timeout still bounds
      // a genuinely missing response.
      if (!packet.byteLength && (options.ignoreEmpty ?? true)) continue;
      const response = parseAdnlResponse(packet);
      // Fastboot/ADNL may emit INFO progress frames while a partition is
      // being committed. They are not terminal responses; keep reading until
      // the device returns OKAY, FAIL, or DATAOUT.
      if (response.status !== "INFO") return response;
      onInfo?.(response.message ?? "");
    }
  }
}

function isSensitivePartition(name: string): boolean {
  return /^(bootloader|tpl|bl2|u-boot|uboot|env|env_ab|gpt|mbr|partition|dtb)$/i.test(name.trim());
}

function formatHex(value: number): string {
  return `0x${value.toString(16)}`;
}

function isBootloaderPartition(name: string): boolean {
  return /^bootloader$/i.test(name.trim());
}

function isOptionalSheaderFailure(message?: string): boolean {
  return /unsupported|not supported|not found|unknown|not available|whitelist/i.test(message ?? "");
}
